import { Link } from '@tanstack/react-router'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import {
  SecondaryButton,
  secondaryLinkClasses,
} from '@workspace/ui/components/dsm/secondary-button'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@workspace/ui/components/sheet'
import { useState } from 'react'
import { CopyLinkRow } from '../../../../components/copy-link-row'
import { useInvestorContext } from '../../../../contexts/investor-guard'
import { useMediaQuery } from '../../../../hooks/use-media-query'
import { copy } from '../../copy/investor-copy'
import { SendWithTiles } from '../send-with-tiles'

export type ShareDraft = {
  startupName: string
  subject: string
  /** The drafted message for each audience. */
  messages: { colleague: string; investor: string }
  link: string | null
  /** The investor's own note on the startup, sent only when the investor includes it. */
  note: string | null
}

type Audience = 'colleague' | 'investor'
const AUDIENCES: Audience[] = ['colleague', 'investor']

/**
 * Share startup: a drafted message to a colleague or an investor at another firm, sent from the investor's own
 * account or copied. The draft comes from the caller, who asked for it when Share was chosen. Nothing is recorded.
 */
export function ShareWindow({ draft, onClose }: { draft: ShareDraft | null; onClose: () => void }) {
  const desktop = useMediaQuery('(min-width: 640px)')
  const open = draft !== null
  const form = draft && <ShareForm key={draft.subject} draft={draft} />
  const title = draft ? copy('share.titleFor', { startup: draft.startupName }) : ''
  if (desktop) {
    return (
      <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
        <DialogContent>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{copy('share.lead')}</DialogDescription>
          {form}
        </DialogContent>
      </Dialog>
    )
  }
  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent side="bottom">
        <SheetTitle>{title}</SheetTitle>
        <SheetDescription>{copy('share.lead')}</SheetDescription>
        {form}
      </SheetContent>
    </Sheet>
  )
}

function ShareForm({ draft }: { draft: ShareDraft }) {
  const { investor } = useInvestorContext()
  const [audience, setAudience] = useState<Audience>('colleague')
  // The link is part of the message text, on its own line at the end, so the box shows what is sent.
  const messageWithLink = (text: string) => (draft.link ? `${text}\n\n${draft.link}` : text)
  const [message, setMessage] = useState(messageWithLink(draft.messages.colleague))
  // Each audience has its own draft; choosing one loads it, and anything typed is replaced.
  const chooseAudience = (next: Audience) => {
    setAudience(next)
    setMessage(messageWithLink(draft.messages[next]))
  }
  const [includeNote, setIncludeNote] = useState(false)
  if (draft.link === null)
    return <p className="text-sm text-muted-foreground">{copy('share.noLink')}</p>

  const body = includeNote && draft.note ? `${message}\n\n${draft.note}` : message
  const withLink = body
  return (
    <div className="space-y-4">
      <fieldset className="flex flex-wrap items-center gap-2">
        <legend className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {copy('share.audience.label')}
        </legend>
        {AUDIENCES.map((option) => (
          <SecondaryButton
            key={option}
            size="sm"
            aria-pressed={audience === option}
            onClick={() => chooseAudience(option)}
            className={audience === option ? 'border-foreground text-foreground' : undefined}
          >
            {copy(option === 'colleague' ? 'share.audience.colleague' : 'share.audience.investor')}
          </SecondaryButton>
        ))}
      </fieldset>
      <textarea
        aria-label={copy('share.message.label')}
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        rows={4}
        className="w-full rounded-lg border border-border bg-[#0F0F10] px-3 py-2.5 text-sm leading-relaxed text-foreground/80 outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
      />
      {audience === 'colleague' && draft.note && (
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            className="size-4 accent-primary"
            checked={includeNote}
            onChange={(event) => setIncludeNote(event.target.checked)}
          />
          {copy('share.note.include')}
        </label>
      )}
      <SendWithTiles text={withLink} subject={draft.subject} />
      <CopyLinkRow
        url={draft.link}
        label={copy('share.copyLink')}
        copiedLabel={copy('share.copied')}
      />
      <p className="text-xs text-muted-foreground">{copy('share.privacy')}</p>
      {audience === 'colleague' && (
        <div className="-mx-1 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <p className="min-w-0 flex-1 text-xs leading-relaxed text-muted-foreground">
            {copy('share.team.line')}
          </p>
          <Link
            to="/app/investor/$id/wallet"
            params={{ id: investor.id }}
            className={secondaryLinkClasses({ size: 'sm' })}
          >
            {copy('share.team.plans')}
          </Link>
        </div>
      )}
    </div>
  )
}
